import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Loader2, UserCheck, Globe, MapPin, Calendar, Briefcase, Heart } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading, updateUser } = useAuth();
  const [form, setForm] = useState({ gender: "", birth_year: "", country: "", city: "", occupation: "", single_status: "", single_years: "", introduction: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading) return;
    // Must be authenticated
    if (!isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    // If already has required fields, skip
    if (user?.gender && user?.birth_year && user.birth_year > 0 && (user?.occupation || user?.job) && user?.single_status) {
      navigate("/community", { replace: true });
      return;
    }
    // Prefill existing values so users don't lose previously entered data
    setForm((f) => ({
      gender: user?.gender || f.gender,
      birth_year: user?.birth_year && user.birth_year > 0 ? String(user.birth_year) : f.birth_year,
      country: user?.country || f.country,
      city: user?.city || f.city,
      occupation: user?.occupation || user?.job || f.occupation,
      single_status: user?.single_status || f.single_status,
      single_years: user?.single_years || f.single_years,
      introduction: user?.introduction || f.introduction,
    }));
  }, [loading, isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.gender) return setError("성별을 선택해주세요.");
    if (!form.birth_year) return setError("출생년도를 입력해주세요.");
    const birthYearInt = parseInt(form.birth_year);
    const currentYear = new Date().getFullYear();
    if (birthYearInt < 1940 || birthYearInt > currentYear - 14) {
      return setError("올바른 출생년도를 입력해주세요.");
    }
    if (!form.country.trim() || !form.city.trim()) return setError("국가와 도시를 모두 입력해주세요.");
    if (!form.occupation.trim()) return setError("직업을 입력해주세요.");
    if (!form.single_status) return setError("싱글 여부를 선택해주세요.");
    if (["divorced", "widowed"].includes(form.single_status) && !form.single_years.trim()) return setError("이혼/사별 경과 기간을 입력해주세요. (예: 3년차)");

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/google/onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          gender: form.gender,
          birth_year: birthYearInt,
          country: form.country.trim(),
          city: form.city.trim(),
          occupation: form.occupation.trim(),
          single_status: form.single_status,
          single_years: form.single_years.trim(),
          introduction: form.introduction.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "프로필 저장에 실패했습니다.");
      if (data.user) updateUser(data.user);
      // 신규 회원은 가입인사 작성을 완료해야 커뮤니티 진입 가능
      if (data.user?.needs_intro === false) {
        navigate("/community", { replace: true });
      } else {
        navigate("/welcome-intro", { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 py-10 px-4" data-testid="onboarding-page">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-burgundy-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-7 h-7 text-burgundy-500" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-stone-800 mb-1">
              환영합니다, {user.username || user.name}님!
            </h1>
            <p className="text-sm text-stone-500">매칭에 필요한 기본 정보를 입력해주세요.</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="onboarding-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">성별 *</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, gender: "male" })}
                    className={`flex-1 py-3 rounded-xl border transition-colors ${form.gender === "male" ? "bg-blue-50 border-blue-300 text-blue-600" : "bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300"}`}
                    data-testid="onboarding-gender-male"
                  >
                    남성
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, gender: "female" })}
                    className={`flex-1 py-3 rounded-xl border transition-colors ${form.gender === "female" ? "bg-rose-50 border-rose-300 text-rose-600" : "bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300"}`}
                    data-testid="onboarding-gender-female"
                  >
                    여성
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">출생년도 *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input
                    type="number"
                    value={form.birth_year}
                    onChange={(e) => setForm({ ...form, birth_year: e.target.value })}
                    min="1940"
                    max={new Date().getFullYear() - 14}
                    placeholder="예: 1975"
                    required
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    data-testid="onboarding-birth-year"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">거주지역 *</label>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="국가 (예: 한국)"
                    required
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    data-testid="onboarding-country"
                  />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="도시 (예: 서울)"
                    required
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    data-testid="onboarding-city"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">직업 *</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input
                    type="text"
                    value={form.occupation}
                    onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                    placeholder="예: 회사원, 자영업"
                    required
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    data-testid="onboarding-occupation"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">싱글 여부 *</label>
                <div className="relative">
                  <Heart className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 z-10 pointer-events-none" />
                  <select
                    value={form.single_status}
                    onChange={(e) => setForm({ ...form, single_status: e.target.value })}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 appearance-none cursor-pointer"
                    data-testid="onboarding-single-status"
                  >
                    <option value="">선택하세요</option>
                    <option value="single">미혼</option>
                    <option value="divorced">이혼</option>
                    <option value="widowed">사별</option>
                  </select>
                </div>
              </div>
            </div>

            {["divorced", "widowed"].includes(form.single_status) && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {form.single_status === "divorced" ? "이혼" : "사별"} 경과 기간 *
                </label>
                <input
                  type="text"
                  value={form.single_years}
                  onChange={(e) => setForm({ ...form, single_years: e.target.value })}
                  placeholder="예: 3년차"
                  className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                  data-testid="onboarding-single-years"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">자기소개 (선택)</label>
              <textarea
                value={form.introduction}
                onChange={(e) => setForm({ ...form, introduction: e.target.value })}
                placeholder="간단한 자기소개를 입력하세요"
                className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 min-h-[80px] resize-none"
                data-testid="onboarding-introduction"
              />
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full btn-primary py-3" data-testid="onboarding-submit">
              {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "시작하기"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
